import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Zap, Eye, Users, Globe, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useInView } from '../hooks/useInView';
import { SEO, schemaOrganization } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(14px)',
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const PILLARS = [
  {
    icon: Eye,
    title: 'Radical Visibility',
    text: 'Emerging fashion talent — the most gifted designers, photographers, stylists, and art directors — remains invisible to the brands that need them most. FashionSpyder changes that by putting verified, richly-profiled professionals in front of the right HR teams.',
  },
  {
    icon: Zap,
    title: 'Intelligence Over Guesswork',
    text: 'Hiring decisions in fashion are still largely intuitive. Our proprietary AI Matchmaker scores every candidate across four dimensions — hard skills, semantic style DNA, social proof, and logistics — giving HR teams a data-backed shortlist in seconds, not weeks.',
  },
  {
    icon: Users,
    title: 'Built for Fashion, Only for Fashion',
    text: 'Generic recruitment platforms don\'t understand the difference between a womenswear RTW designer and a knitwear specialist. Every filter, every scoring vector, every outreach template on FashionSpyder is built with deep fashion industry knowledge baked in.',
  },
  {
    icon: Globe,
    title: 'Flence Ecosystem',
    text: 'FashionSpyder is part of the Flence family — the professional community for fashion creatives. Candidate profiles flow directly from Flence, verified and enriched, ensuring every profile in the gallery represents a real, active professional ready to work.',
  },
];

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About FashionSpyder — The Story Behind the Platform"
        description="FashionSpyder is the first AI-powered HR intelligence platform built exclusively for the fashion industry. Founded in Milan, we help fashion brand HR teams discover, rank and hire the best creative talent."
        path="/about"
        schema={schemaOrganization}
      />

      {/* Hero */}
      <section className="py-24 lg:py-36 px-6 lg:px-20 border-b border-border relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-[hsl(var(--primary)/0.35)]" />
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">

          <div className="lg:col-span-7 space-y-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground border border-border px-3 py-1.5" style={{ fontFamily: MONO }}>
                <span className="w-1.5 h-1.5 bg-[hsl(var(--primary))]" />
                Milan, Italy · Est. 2025
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1
                className="text-5xl lg:text-7xl font-semibold tracking-[-0.03em] leading-[1.0]"
                style={{ fontFamily: SERIF }}
              >
                The intelligence layer fashion hiring was missing.
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                FashionSpyder was built to solve a specific, persistent problem:
                exceptional fashion talent is everywhere — but invisible to the brands
                that could change their careers.
              </p>
            </Reveal>
          </div>

          <Reveal delay={200} className="lg:col-span-5">
            <div className="bg-[hsl(var(--secondary))] border border-border p-8">
              <p
                className="text-2xl font-semibold leading-snug mb-6"
                style={{ fontFamily: SERIF }}
              >
                "Great fashion is made by great people. Finding them shouldn't take months."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 border border-border flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <p className="text-sm font-semibold">The FashionSpyder Team</p>
                  <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>Flence Ecosystem · Milan</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The problem */}
      <section className="py-20 lg:py-28 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            <Reveal className="lg:col-span-4 lg:sticky lg:top-24">
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-4" style={{ fontFamily: MONO }}>
                The Problem
              </span>
              <h2
                className="text-3xl lg:text-4xl font-semibold leading-tight"
                style={{ fontFamily: SERIF }}
              >
                Fashion hiring has been broken for too long.
              </h2>
            </Reveal>

            <div className="lg:col-span-8 space-y-6">
              {[
                {
                  n: '01',
                  title: 'Low visibility for emerging talent',
                  text: 'The most talented emerging designers, photographers, stylists, and art directors — the ones who will define the next decade of fashion — are scattered across fragmented portfolios, social feeds, and local networks. Without institutional connections, they simply don\'t appear on a brand\'s radar.',
                },
                {
                  n: '02',
                  title: 'Inefficient, relationship-dependent hiring',
                  text: 'Fashion hiring still relies heavily on word-of-mouth, agency relationships, and expensive headhunters. For brands trying to scale teams quickly or hire for niche specialisms (knitwear, technical design, sustainable sourcing), the existing process is slow, costly, and often circular.',
                },
                {
                  n: '03',
                  title: 'No platform built for fashion\'s specificity',
                  text: 'Generic job boards and LinkedIn don\'t understand fashion\'s vocabulary — CLO 3D, draping, RTW, style DNA, portfolio authority. Matching a luxury minimalist brand with a maximalist designer wastes everyone\'s time. Fashion needs a tool built from the inside out.',
                },
              ].map((item, i) => (
                <Reveal key={item.n} delay={i * 80}>
                  <div className="flex gap-6 border-b border-border pb-6 last:border-0">
                    <span className="text-3xl font-bold text-[hsl(var(--primary)/0.3)] flex-shrink-0 leading-none" style={{ fontFamily: SERIF }}>
                      {item.n}
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What we built */}
      <section className="py-20 lg:py-28 px-6 lg:px-20 bg-[hsl(var(--secondary))] border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-4" style={{ fontFamily: MONO }}>
              Our Answer
            </span>
          </Reveal>
          <Reveal delay={60}>
            <h2
              className="text-3xl lg:text-5xl font-semibold leading-tight mb-14 max-w-3xl"
              style={{ fontFamily: SERIF }}
            >
              A proprietary AI platform that works the way fashion actually works.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((pillar, i) => (
              <Reveal key={pillar.title} delay={i * 70}>
                <div className="bg-white border border-border p-7 h-full">
                  <div className="w-9 h-9 border border-border flex items-center justify-center mb-5">
                    <pillar.icon className="w-4 h-4 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-3">{pillar.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{pillar.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-20 lg:py-28 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            <Reveal>
              <div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-4" style={{ fontFamily: MONO }}>
                  Who We Are
                </span>
                <h2
                  className="text-3xl lg:text-4xl font-semibold leading-tight mb-6"
                  style={{ fontFamily: SERIF }}
                >
                  From Milan. Built by people who know fashion from the inside.
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    FashionSpyder was created by the team behind <a href="https://flence.com" target="_blank" rel="noreferrer" className="text-foreground font-semibold underline decoration-[hsl(var(--primary))] underline-offset-2">Flence</a> —
                    the creative professional community that has been connecting fashion talent with opportunity since
                    its founding. Based in Milan, the fashion capital that sits at the intersection of craftsmanship
                    and commerce, we bring first-hand industry knowledge to every product decision we make.
                  </p>
                  <p>
                    Our team combines backgrounds in fashion design, editorial, brand management,
                    and AI engineering. We've worked with the brands. We've hired the talent.
                    We've felt the frustration — on both sides of the process.
                  </p>
                  <p>
                    FashionSpyder is our answer: precise, purposeful, and built for the professionals
                    who care deeply about getting it right.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="space-y-4">
                {[
                  { label: 'Founded', value: '2025' },
                  { label: 'Location', value: 'Milan, Italy' },
                  { label: 'Ecosystem', value: 'Flence · Fashion Professional Community' },
                  { label: 'Exclusive access', value: 'Fashion brand HR teams only' },
                  { label: 'Candidate source', value: 'Verified Flence professionals' },
                  { label: 'AI engine', value: 'Proprietary matching algorithm' },
                ].map(item => (
                  <div key={item.label} className="flex items-baseline gap-4 border-b border-border pb-4 last:border-0">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground min-w-[140px]" style={{ fontFamily: MONO }}>
                      {item.label}
                    </span>
                    <span className="text-sm font-medium text-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-20">
        <div className="max-w-[800px] mx-auto">
          <Reveal>
            <div className="w-10 h-[2px] bg-[hsl(var(--primary))] mb-10" />
          </Reveal>
          <Reveal delay={60}>
            <h2
              className="text-4xl lg:text-5xl font-semibold tracking-tight mb-6 leading-[1.05]"
              style={{ fontFamily: SERIF }}
            >
              Ready to hire fashion's next generation of talent?
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
              Create your account and start discovering Europe's most exclusive fashion talent today.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.88)] text-white border-0 px-8 py-6 h-auto text-base font-medium gap-2"
              >
                Get Started <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/pricing')}
                className="border-border hover:border-foreground px-8 py-6 h-auto text-base"
              >
                View Pricing
              </Button>
            </div>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-sm text-muted-foreground mt-8">
              Are you a fashion professional? See how to get discovered on{' '}
              <Link to="/for-talent" className="text-[hsl(var(--primary))] font-medium hover:underline">For Talent</Link>,
              or read our <Link to="/faq" className="text-[hsl(var(--primary))] font-medium hover:underline">FAQ</Link>.
            </p>
          </Reveal>
        </div>
      </section>

    </div>
  );
};

export default About;
